import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PricingPlan } from "@/model/interface/PricingPlan";
import React from "react";
import RateField from "./RateField";

interface PlanTextingRatesSectionProps {
  editing: Partial<PricingPlan>;
  onField: (field: string, value: unknown) => void;
}

const CAMPAIGN_FEES: { field: keyof PricingPlan; label: string }[] = [
  { field: "a2pCampaignStandardBaseRate", label: "Standard campaign" },
  { field: "a2pCampaignLowVolumeBaseRate", label: "Low-volume campaign" },
  { field: "a2pCampaignSoleProprietorBaseRate", label: "Sole proprietor campaign" },
];

export default function PlanTextingRatesSection({ editing, onField }: PlanTextingRatesSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Texting registration</CardTitle>
        <CardDescription>
          One-time registration fees charged from the balance, plus the carrier's monthly campaign fee per tier. The
          monthly fee is sold through its Stripe price under Add-ons; the carrier fee here is what at-cost plans are
          discounted down to.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RateField
          label="Brand registration (one-time)"
          baseRate={editing.a2pBrandBaseRate ?? 0}
          markupType={editing.a2pBrandMarkupType ?? "FLAT"}
          markupAmount={editing.a2pBrandMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("a2pBrandBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("a2pBrandMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("a2pBrandMarkupAmount", newMarkupAmount)}
        />
        <RateField
          label="Low-volume brand registration (one-time)"
          baseRate={editing.a2pBrandLowVolumeBaseRate ?? 0}
          markupType={editing.a2pBrandLowVolumeMarkupType ?? "FLAT"}
          markupAmount={editing.a2pBrandLowVolumeMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("a2pBrandLowVolumeBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("a2pBrandLowVolumeMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("a2pBrandLowVolumeMarkupAmount", newMarkupAmount)}
        />
        <RateField
          label="Campaign vetting (one-time)"
          baseRate={editing.a2pCampaignVettingBaseRate ?? 0}
          markupType={editing.a2pCampaignVettingMarkupType ?? "FLAT"}
          markupAmount={editing.a2pCampaignVettingMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("a2pCampaignVettingBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("a2pCampaignVettingMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("a2pCampaignVettingMarkupAmount", newMarkupAmount)}
        />
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Assisted registration fee (flat, one-time)</Label>
          <Input
            type="number"
            step="0.01"
            className="max-w-[200px]"
            value={editing.a2pAssistedRegistrationFee ?? 0}
            onChange={(changeEvent) => onField("a2pAssistedRegistrationFee", parseFloat(changeEvent.target.value) || 0)}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Carrier monthly campaign fee ($)</Label>
          <div className="grid grid-cols-3 gap-2">
            {CAMPAIGN_FEES.map((fee) => (
              <div key={fee.field}>
                <span className="text-[10px] text-muted-foreground">{fee.label}</span>
                <Input
                  type="number"
                  step="0.01"
                  value={(editing[fee.field] as number | undefined) ?? 0}
                  onChange={(changeEvent) => onField(fee.field, parseFloat(changeEvent.target.value) || 0)}
                />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
